import { c as _c } from "react/compiler-runtime";
import type { CSSObject } from "@mui/material/styles";
import type { RefObject } from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LANDING_DECOR_GLASS_OPACITY_SX } from "#/features/homePage/lib/landingDecor3dConstants";
import { useMobileLayout } from "#/shared/hooks";
import { prefersReducedMotion } from "#/shared/lib/prefersReducedMotion";
export type LandingDecor3dMobileEntranceVariant = "hero" | "python";
const easing = "cubic-bezier(0.22, 1, 0.36, 1)";
const scheduleEnteredTrue = (setEntered: (value: boolean) => void, isMountedRef: RefObject<boolean>) => {
  let outerFrame = 0;
  let innerFrame = 0;
  outerFrame = window.requestAnimationFrame(() => {
    innerFrame = window.requestAnimationFrame(() => {
      if (isMountedRef.current) {
        setEntered(true);
      }
    });
  });
  return () => {
    window.cancelAnimationFrame(outerFrame);
    window.cancelAnimationFrame(innerFrame);
  };
};

/**
 * Mobile-only entrance (fade + slide + scale) for landing 3D decor. Desktop unchanged.
 * Hero runs shortly after mount; Python runs when `visibilityAnchorRef` intersects the viewport.
 * Respects `prefers-reduced-motion`.
 */
export const useLandingDecor3dMobileEntrance = (variant, visibilityAnchorRef) => {
  const $ = _c(21);
  const isMobile = useMobileLayout();
  const [entered, setEntered] = useState(false);
  const isMountedRef = useRef(true);
  const prevIsMobileRef = useRef(isMobile);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = typeof window !== "undefined" && prefersReducedMotion();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const reduceMotion = t0;
  const skipEntranceAnimation = !isMobile || reduceMotion;
  const effectiveEntered = skipEntranceAnimation || entered;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== isMobile) {
    t3 = () => {
      const wasMobile = prevIsMobileRef.current;
      prevIsMobileRef.current = isMobile;
      if (!isMobile || reduceMotion) {
        return;
      }
      if (wasMobile) {
        return;
      }
      queueMicrotask(() => {
        setEntered(false);
      });
    };
    t4 = [isMobile, reduceMotion];
    $[3] = isMobile;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== skipEntranceAnimation || $[7] !== variant) {
    t5 = () => {
      if (skipEntranceAnimation || variant !== "hero") {
        return;
      }
      return scheduleEnteredTrue(setEntered, isMountedRef);
    };
    t6 = [skipEntranceAnimation, variant];
    $[6] = skipEntranceAnimation;
    $[7] = variant;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== skipEntranceAnimation || $[11] !== variant || $[12] !== visibilityAnchorRef) {
    t7 = () => {
      if (skipEntranceAnimation || variant !== "python") {
        return;
      }
      const anchor = visibilityAnchorRef?.current;
      if (!anchor) {
        return;
      }
      let cancelScheduledEnter;
      if (typeof IntersectionObserver !== "function") {
        cancelScheduledEnter = scheduleEnteredTrue(setEntered, isMountedRef);
        return () => {
          cancelScheduledEnter?.();
        };
      }
      const observer = new IntersectionObserver(entries => {
        const hit = entries.some(_temp);
        if (!hit) {
          return;
        }
        observer.disconnect();
        cancelScheduledEnter?.();
        cancelScheduledEnter = scheduleEnteredTrue(setEntered, isMountedRef);
      }, {
        root: null,
        rootMargin: "0px 0px -6% 0px",
        threshold: 0
      });
      observer.observe(anchor);
      return () => {
        observer.disconnect();
        cancelScheduledEnter?.();
      };
    };
    t8 = [skipEntranceAnimation, variant, visibilityAnchorRef];
    $[10] = skipEntranceAnimation;
    $[11] = variant;
    $[12] = visibilityAnchorRef;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useLayoutEffect(t7, t8);
  let t9;
  bb0: {
    if (!isMobile) {
      let t10;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = {};
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      t9 = t10;
      break bb0;
    }
    const fromTransform = variant === "hero" ? "translate3d(36px, 20px, 0) scale(0.88)" : "translate3d(-36px, 20px, 0) scale(0.88)";
    if (effectiveEntered) {
      let t10;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = {
          opacity: LANDING_DECOR_GLASS_OPACITY_SX.xs,
          transform: "translate3d(0, 0, 0) scale(1)",
          transition: `opacity 0.8s ${easing}, transform 0.8s ${easing}`,
          willChange: "auto"
        };
        $[16] = t10;
      } else {
        t10 = $[16];
      }
      t9 = t10;
      break bb0;
    }
    let t10;
    if ($[17] !== fromTransform) {
      t10 = {
        opacity: 0,
        transform: fromTransform,
        transition: "none",
        willChange: "opacity, transform"
      };
      $[17] = fromTransform;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    t9 = t10;
  }
  const mobileEntranceSx = t9;
  let t10;
  if ($[19] !== mobileEntranceSx) {
    t10 = {
      mobileEntranceSx
    };
    $[19] = mobileEntranceSx;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};
function _temp(entry) {
  return entry.isIntersecting;
}